import {
  BodyText,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from "@/components/ui";
import { RxHistoryConsentFooter } from "@/features/Patient/MedicalRecords/RxHistory/RxHistoryConsentFooter";
import { RxHistoryListSkeleton } from "@/features/Patient/MedicalRecords/RxHistory/RxHistoryListSkeleton";
import { RxHistoryMedicationList } from "@/features/Patient/MedicalRecords/RxHistory/RxHistoryMedicationList";
import {
  RxHistoryStatus,
  useRxHistory,
} from "@/features/Patient/MedicalRecords/RxHistory/network/useRxHistory";
import { useMemo, useState } from "react";

interface RxHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
}

export const RxHistoryModal = ({
  open,
  onOpenChange,
  patientId,
}: RxHistoryModalProps) => {
  const [consentGranted, setConsentGranted] = useState(false);

  const {
    data: rxHistoryData,
    isLoading: isHistoryLoading,
    isPollingActive,
    isError: isHistoryError,
    refetch: refetchHistory,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useRxHistory({
    patientId,
    enabled: open && consentGranted,
  });

  // Flatten all pages into a single array of medications
  const medications = useMemo(() => {
    if (!rxHistoryData?.pages) return [];
    return rxHistoryData.pages.flatMap((page) => page.rxHistory);
  }, [rxHistoryData?.pages]);

  const status = rxHistoryData?.pages[0]?.status;

  const isPolling = consentGranted && isPollingActive && !isHistoryError;

  const isTimedOut =
    !isHistoryLoading && !isPollingActive && status === RxHistoryStatus.SENT;

  const isReady =
    !isHistoryLoading &&
    !isPollingActive &&
    status === RxHistoryStatus.FINISHED;

  const renderContent = () => {
    if (!consentGranted)
      return (
        <div className="flex flex-col gap-3">
          <BodyText size="sm" textColor="tertiary">
            Certain information may not be available or accurate in this report,
            including items that the patient asked not be disclosed due to
            patient privacy concerns, over the-counter medications, low cost
            prescriptions, prescriptions paid for by the patient or
            nonparticipating sources, or errors in insurance claims information.
          </BodyText>
          <BodyText size="sm" textColor="tertiary">
            There may also be a potential for false positive match if the
            patient’s demographics sent has information similar to another
            patient. The provider should verify the patient’s demographics and
            medication history with the patient.
          </BodyText>
        </div>
      );

    if (isHistoryLoading || isPolling) {
      return (
        <div className="flex flex-col items-center gap-3 py-6">
          <BodyText size="sm" textColor="tertiary">
            Retrieving medication history...
          </BodyText>
          <RxHistoryListSkeleton count={3} />
        </div>
      );
    }

    if (isHistoryError || isTimedOut) {
      return (
        <RxHistoryMedicationList
          medications={[]}
          isLoading={false}
          isError
          onRetry={() => refetchHistory()}
        />
      );
    }

    if (isReady) {
      return (
        <RxHistoryMedicationList
          medications={medications}
          isLoading={false}
          isError={false}
          onRetry={() => refetchHistory()}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
        />
      );
    }

    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogOverlay />
      <DialogContent className="w-full max-w-3xl max-h-[80vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Medication History</DialogTitle>
          <DialogDescription className="hidden">
            Patient medication history from SureScripts
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">{renderContent()}</div>

        <RxHistoryConsentFooter
          patientId={patientId}
          consentGranted={consentGranted}
          onConsentGranted={() => setConsentGranted(true)}
        />
      </DialogContent>
    </Dialog>
  );
};
